import { forwardRef, useState, useCallback, useRef, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import useSearchParams from '@Hooks/useSearchParams';
import * as ModalWindows from '@Stores/ModalWindows';
import ReviewsSection from '@Pages/PageProduct/elements/ReviewsSection';
import ListReviews from '@Pages/PageProduct/elements/ListReviews';
import EmptyReview from '@Pages/PageProduct/elements/ReviewsSection/elements/EmptyReview';
import useScrollToRef from '../../../hooks/useScrollToRef';
import styles from './Reviews.module.css';

import type { ProductData } from '@Types/Product';
import type { HTMLAttributes, MouseEvent, MutableRefObject } from 'react';
import type { ReviewData } from '@Types/Review';
import type { SelectedReviewsData } from '@Pages/PageProduct/elements/ReviewsSection';

interface ReviewsProps extends HTMLAttributes<HTMLSelectElement> {
  reviewsSubgallery: ReviewData[];
  product: ProductData;
  refActionBar: MutableRefObject<HTMLDivElement>;
}

const Reviews = forwardRef<HTMLDivElement, ReviewsProps>((props, ref) => {
  const { reviewsSubgallery, product, refActionBar, className, ...restProps } = props;
  const refReviewsList = useRef<HTMLDivElement>();
  const { handleScrollTo } = useScrollToRef();
  const searchParams = useSearchParams();
  const [selectedReviews, setSelectedReviews] = useState<SelectedReviewsData>('all');
  const [nowPageReviews, setNowPageReviews] = useState(Number(searchParams.get('page') || 1));

  const siteReviews = useMemo(() => {
    const all = (reviewsSubgallery || []).filter((review) => {
      return review.source === 'site';
    });
    const withPhoto = all.filter((review) => review.photos?.length > 0);

    return { all, withPhoto };
  }, [reviewsSubgallery]);

  const isReviews = reviewsSubgallery.length > 0 && siteReviews.all.length > 0;

  const handleAddReview = useCallback(() => {
    ModalWindows.open('SendReview', { product: product });
  }, [product]);

  const handleChangeSelectedReviews = useCallback((e: MouseEvent, kind: SelectedReviewsData) => {
    setSelectedReviews(kind);
    setNowPageReviews(1);
  }, []);

  const handleChangePage = useCallback(
    (e: MouseEvent, pageNum: number) => {
      setNowPageReviews(pageNum);
      handleScrollTo(refReviewsList, refActionBar.current?.clientHeight);
    },
    [handleScrollTo, refReviewsList, refActionBar],
  );

  return (
    <section
      {...restProps}
      ref={ref}
      data-testid='feedback'
      id='reviews-section'
      className={cn({ [styles.empty]: !isReviews }, className)}
    >
      {isReviews ? (
        <>
          <div className={styles.sectionReviews}>
            <ReviewsSection
              reviews={reviewsSubgallery}
              onAddReview={handleAddReview}
              onChangeSelectedReviews={handleChangeSelectedReviews}
            />
            <div className={styles.wrapperListReviews} ref={refReviewsList}>
              <ListReviews
                className={styles.listReviews}
                reviews={selectedReviews === 'all' ? siteReviews.all : siteReviews.withPhoto}
                onChangePage={handleChangePage}
                nowPage={nowPageReviews}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyReviewWrapper}>
          <EmptyReview onAddReview={handleAddReview} />
        </div>
      )}
    </section>
  );
});

Reviews.displayName = 'Reviews';

export default Reviews;
